import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
    DollarSign, CheckCircle2, AlertCircle, XCircle,
    TrendingUp, TrendingDown, Minus, MapPin, Layers,
    Monitor, Tag, Percent, Eye, ChevronDown, Check, Sparkles
} from 'lucide-react';
import type { SimulateNewRuleResponse, SimulateNewRuleRequest, GranulationResponse, SegmentationOptions } from '@/types/price-rules';

interface Props {
    simulation: SimulateNewRuleResponse | null;
    formData: Partial<SimulateNewRuleRequest>;
    manualPrice: number | null;
    setManualPrice: (price: number | null) => void;
    granulation: GranulationResponse | undefined;
    isLoading: boolean;
    segmentationOptions: SegmentationOptions | undefined;
}

export default function StepSimulation({ simulation, formData, manualPrice, setManualPrice, granulation, isLoading, segmentationOptions }: Props) {
    const [useManualPrice, setUseManualPrice] = useState(false);
    const [priceSelectOpen, setPriceSelectOpen] = useState(false);

    const formatPrice = (price: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
    const formatNumber = (num: number) => new Intl.NumberFormat('pt-BR').format(num);

    const finalPrice = manualPrice ?? simulation?.suggested_price ?? 0;

    const getCountryLabel = (codigo: string) => {
        if (codigo === '*') return 'Todos os países';
        const c = segmentationOptions?.countries.find(c => c.codigo === codigo);
        return c ? `${c.nome} (${c.codigo})` : codigo;
    };

    const getNearestGranulationPrice = (price: number) => {
        if (!granulation?.values.length) return price;
        return granulation.values.reduce((prev, curr) => Math.abs(curr - price) < Math.abs(prev - price) ? curr : prev);
    };

    const scaleInfo = (simulation as any)?.scale_info;
    const basePrice = (simulation as any)?.base_price;

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                </div>
                <p className="mt-6 text-lg font-medium text-gray-700">Consultando GAM...</p>
                <p className="mt-2 text-sm text-gray-500">Buscando dados de performance</p>
            </div>
        );
    }

    if (!simulation) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <XCircle className="h-8 w-8 text-gray-400" />
                </div>
                <p className="mt-4 text-gray-600">Nenhuma simulação disponível.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Resultado da Simulação</h2>
                <p className="text-sm text-gray-500 mt-1">Dados consultados diretamente do GAM.</p>
            </div>

            {/* Segmentation Summary */}
            <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Segmentação consultada</h4>
                <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-200 shadow-sm">
                        <MapPin className="h-3.5 w-3.5 text-green-600" />
                        {getCountryLabel(formData.country || '*')}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-200 shadow-sm">
                        <Layers className="h-3.5 w-3.5 text-blue-600" />
                        {formData.ad_unit === '*' ? 'Todos blocos' : formData.ad_unit}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-200 shadow-sm">
                        <Monitor className="h-3.5 w-3.5 text-purple-600" />
                        {formData.device === '*' ? 'Todos devices' : formData.device}
                    </span>
                    {formData.custom_key && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 text-sm rounded-lg border border-orange-200 shadow-sm">
                            <Tag className="h-3.5 w-3.5" />
                            {formData.custom_key}={formData.custom_value}
                        </span>
                    )}
                </div>
            </div>

            {!simulation.has_data ? (
                <div className="p-8 bg-red-50 rounded-xl border-2 border-red-200 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                        <XCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-red-900">Nenhum dado encontrado</h3>
                    <p className="mt-2 text-red-700">{simulation.message || 'O GAM não retornou dados para essa segmentação.'}</p>
                    <p className="mt-2 text-sm text-red-600">Verifique os filtros e tente novamente.</p>
                </div>
            ) : (
                <>
                    {/* Metrics */}
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-emerald-100 rounded-md">
                                    <DollarSign className="h-4 w-4 text-emerald-600" />
                                </div>
                                <span className="text-sm text-gray-500">eCPM Atual</span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{formatPrice(simulation.metrics?.ecpm || 0)}</p>
                        </div>

                        <div className="p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-blue-100 rounded-md">
                                    <Percent className="h-4 w-4 text-blue-600" />
                                </div>
                                <span className="text-sm text-gray-500">PMR Atual</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <p className="text-2xl font-bold text-gray-900">{simulation.metrics?.pmr.toFixed(1)}%</p>
                                <span className="text-sm text-gray-400">/ {formData.pmr_target}% meta</span>
                            </div>
                        </div>

                        <div className="p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-purple-100 rounded-md">
                                    <Eye className="h-4 w-4 text-purple-600" />
                                </div>
                                <span className="text-sm text-gray-500">Impressões</span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{formatNumber(simulation.metrics?.impressions || 0)}</p>
                        </div>
                    </div>

                    {/* Scale Info */}
                    {scaleInfo && (
                        <div className={`p-5 rounded-xl border-2 ${
                            scaleInfo.direction === 'up' ? 'bg-emerald-50 border-emerald-200' :
                                scaleInfo.direction === 'down' ? 'bg-red-50 border-red-200' :
                                    'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex items-start gap-4">
                                <div className={`p-2.5 rounded-xl ${
                                    scaleInfo.direction === 'up' ? 'bg-emerald-100' :
                                        scaleInfo.direction === 'down' ? 'bg-red-100' :
                                            'bg-gray-100'
                                }`}>
                                    {scaleInfo.direction === 'up' && <TrendingUp className="h-6 w-6 text-emerald-600" />}
                                    {scaleInfo.direction === 'down' && <TrendingDown className="h-6 w-6 text-red-600" />}
                                    {scaleInfo.direction === 'hold' && <Minus className="h-6 w-6 text-gray-500" />}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <h4 className={`font-semibold ${
                                            scaleInfo.direction === 'up' ? 'text-emerald-900' :
                                                scaleInfo.direction === 'down' ? 'text-red-900' :
                                                    'text-gray-900'
                                        }`}>
                                            {scaleInfo.direction === 'up' ? 'Aumento de Preço' :
                                                scaleInfo.direction === 'down' ? 'Redução de Preço' :
                                                    'Preço Mantido'}
                                        </h4>
                                        {scaleInfo.scale_percentage > 0 && (
                                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                                                scaleInfo.direction === 'up' ? 'bg-emerald-200 text-emerald-800' :
                                                    scaleInfo.direction === 'down' ? 'bg-red-200 text-red-800' :
                                                        'bg-gray-200 text-gray-800'
                                            }`}>
                                                {scaleInfo.direction === 'up' ? '+' : '-'}{scaleInfo.scale_percentage}%
                                            </span>
                                        )}
                                    </div>
                                    <p className={`text-sm mt-1 ${
                                        scaleInfo.direction === 'up' ? 'text-emerald-700' :
                                            scaleInfo.direction === 'down' ? 'text-red-700' :
                                                'text-gray-600'
                                    }`}>
                                        {scaleInfo.reason}
                                    </p>

                                    <div className="mt-4 flex items-center gap-2 text-sm">
                                        <div className="px-3 py-1.5 bg-white/60 rounded-lg">
                                            <span className="text-gray-500">Base:</span>
                                            <span className="ml-1 font-semibold">{formatPrice(basePrice || 0)}</span>
                                        </div>
                                        <span className="text-gray-400">→</span>
                                        <div className="px-3 py-1.5 bg-white/60 rounded-lg">
                                            <span className="text-gray-500">Δ PMR:</span>
                                            <span className="ml-1 font-semibold">{scaleInfo.pmr_difference > 0 ? '+' : ''}{scaleInfo.pmr_difference}%</span>
                                        </div>
                                        <span className="text-gray-400">→</span>
                                        <div className={`px-3 py-1.5 rounded-lg font-bold ${
                                            scaleInfo.direction === 'up' ? 'bg-emerald-200 text-emerald-800' :
                                                scaleInfo.direction === 'down' ? 'bg-red-200 text-red-800' :
                                                    'bg-gray-200 text-gray-800'
                                        }`}>
                                            {formatPrice(simulation.suggested_price || 0)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Manual Price Option */}
                    <div className="p-4 bg-white rounded-xl border-2 border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                    <Sparkles className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Usar price manual</p>
                                    <p className="text-xs text-gray-500">Defina um valor diferente do sugerido</p>
                                </div>
                            </div>
                            <Switch
                                checked={useManualPrice}
                                onCheckedChange={(v) => {
                                    setUseManualPrice(v);
                                    if (!v) setManualPrice(null);
                                }}
                            />
                        </div>

                        {useManualPrice && (
                            <div className="mt-4 pt-4 border-t border-gray-100 grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-xs text-gray-500">Digite o valor</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">R$</span>
                                        <Input
                                            type="number"
                                            min={0.5}
                                            max={3000}
                                            step="0.5"
                                            className="h-11 pl-12 bg-white border-2 border-gray-200 rounded-xl focus:border-amber-500"
                                            placeholder={String(simulation.suggested_price || 0)}
                                            value={manualPrice || ''}
                                            onChange={(e) => {
                                                const v = parseFloat(e.target.value);
                                                setManualPrice(isNaN(v) ? null : getNearestGranulationPrice(v));
                                            }}
                                        />
                                    </div>
                                </div>

                                {granulation && (
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-500">Ou selecione da lista</label>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setPriceSelectOpen(!priceSelectOpen)}
                                                className="w-full h-11 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-amber-500"
                                            >
                                                <span className={!manualPrice ? 'text-gray-500' : ''}>
                                                    {manualPrice ? formatPrice(manualPrice) : 'Selecione...'}
                                                </span>
                                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${priceSelectOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {priceSelectOpen && (
                                                <>
                                                    <div className="fixed inset-0 z-10" onClick={() => setPriceSelectOpen(false)} />
                                                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                        <div className="max-h-[200px] overflow-y-auto">
                                                            {granulation.values
                                                                .filter(v => v >= (simulation.suggested_price || 1) * 0.5 && v <= (simulation.suggested_price || 1) * 2)
                                                                .map((v) => (
                                                                    <button
                                                                        key={v}
                                                                        type="button"
                                                                        onClick={() => { setManualPrice(v); setPriceSelectOpen(false); }}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                                            manualPrice === v ? 'bg-amber-50 text-amber-700 font-medium' : ''
                                                                        }`}
                                                                    >
                                                                        <span>
                                                                            {formatPrice(v)}
                                                                            {v === simulation.suggested_price && (
                                                                                <span className="ml-2 text-xs text-gray-500">(sugerido)</span>
                                                                            )}
                                                                        </span>
                                                                        {manualPrice === v && <Check className="h-4 w-4" />}
                                                                    </button>
                                                                ))}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Final Price */}
                    <div className={`p-5 rounded-xl border-2 ${
                        simulation.can_create
                            ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                            : 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200'
                    }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {simulation.can_create ? (
                                    <div className="p-2.5 bg-blue-100 rounded-xl">
                                        <CheckCircle2 className="h-6 w-6 text-blue-600" />
                                    </div>
                                ) : (
                                    <div className="p-2.5 bg-yellow-100 rounded-xl">
                                        <AlertCircle className="h-6 w-6 text-yellow-600" />
                                    </div>
                                )}
                                <div>
                                    <h3 className={`font-semibold ${simulation.can_create ? 'text-blue-900' : 'text-yellow-900'}`}>
                                        {simulation.can_create ? 'Pronto para criar!' : 'Atenção'}
                                    </h3>
                                    {simulation.message && (
                                        <p className={`text-sm ${simulation.can_create ? 'text-blue-700' : 'text-yellow-700'}`}>
                                            {simulation.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                    {manualPrice ? 'Price Manual' : 'Price Sugerido'}
                                </p>
                                <p className={`text-3xl font-bold ${simulation.can_create ? 'text-blue-900' : 'text-yellow-900'}`}>
                                    {formatPrice(finalPrice)}
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}